import Head from 'next/head';
import React from 'react';
import App from 'next/app';
import { Provider } from 'react-redux';
import withRedux from 'next-redux-wrapper';
import { initStore } from '@/store/store';
import { setCookie, parseCookies, destroyCookie } from '@/lib/cookies';
import dynamic from 'next/dynamic';
import * as Sentry from '@sentry/nextjs';
import posthog from '@/lib/posthog'; // do not delete, needed to load the script
import Router from 'next/router';
import { trackPageView, trackLanding } from '@/lib/pageTracking';
import HelpButton from '@/components/HelpButton';
import WebSocketConnection from '@/components/WebSocketConnection';
import VersionUpdateBanner from '@/components/Base/VersionUpdateBanner';
import { Auth0Provider } from '@auth0/nextjs-auth0';

import '@/styles/app.scss';

import { TIMEZONE_LIST } from '@/store/models/profile';
import { fetchCurrentUser, updateTimezone } from '@/store/actions/currentUser.actions';
import { fetchCurrentAccount } from '@/store/actions/currentAccount.actions';
import { fetchActiveFeatureFlag } from '@/store/actions/admin/users.actions';
import { handleUnauthorizedError, sanitizeRedirectPath } from '@/utils/auth';
import { health, fetchWebpageInfo } from '@/store/actions/public.actions';
import { config } from '@/config';
import { getEnv } from '@/lib/env';

// Heavy components loaded only on client to reduce initial bundle (Twilio SDK, interactjs, etc.)
const Dialer = dynamic(() => import('@/components/Dialer/Dialer'), { ssr: false });
const ChatPopup = dynamic(() => import('@/components/ChatPopup'), { ssr: false });
const AiAgentChat = dynamic(() => import('@/components/AiAgentChat'), { ssr: false });
const Sidekick = dynamic(() => import('@/components/Sidekick/Sidekick'), { ssr: false });

// import { subscribe, unsubscribe } from 'pubsub-js';
import Script from 'next/script';

const FORBIDDEN_PAGES_DOMAINS = ['api', 'platform', 'www'];

// /[subdomain] — public campaign pages (/{slug}) and agent webpages (subdomain host)
const PUBLIC_ROUTES = [
  '/login',
  '/login-app',
  '/reset-password',
  '/contact-us',
  '/privacy-policy',
  '/terms-of-use',
  '/[subdomain]'
];
const AUTH_EXCLUDED_ROUTES = [...PUBLIC_ROUTES, '/api/auth/callback', '/sso-logout'];
const CHAT_EXCLUDED_ROUTES = [...PUBLIC_ROUTES, '/wallets/receipt'];
const CONTENT_CENTER_HOME = '/content-center';
const CONTENT_CENTER_ALLOWED_ROUTES = [...AUTH_EXCLUDED_ROUTES, '/account/profile', '/_error'];
const CONTENT_CENTER_ADMIN_ROUTES = ['/admin/users', '/admin/company', '/admin/settings'];
const isContentCenterRoute = route =>
  route.startsWith('/content-center') ||
  CONTENT_CENTER_ALLOWED_ROUTES.includes(route) ||
  CONTENT_CENTER_ADMIN_ROUTES.some(prefix => route.startsWith(prefix)) ||
  (config().IS_DEV_STAGE_DEMO && route === '/admin/flipflop');

class MyApp extends App {
  static async getInitialProps({ Component, ctx, router }) {
    const rootDomain = await MyApp.getRootDomain(ctx);
    await MyApp.setDomainCookie(ctx);
    const webpageUsername = await MyApp.webpageUsername(ctx, rootDomain);
    const { token, redirect_path } = parseCookies(ctx);
    const redirectFromTheThirdService = !token && '/account/external_accounts' === router.route;
    if (!redirectFromTheThirdService && !webpageUsername && !AUTH_EXCLUDED_ROUTES.includes(router.route)) {
      !ctx.store.getState().currentUser?.id && (await MyApp.fetchCurrentUser(ctx));
      !ctx.store.getState().currentAccount?.info?.id && (await MyApp.fetchCurrentAccount(ctx));
      await MyApp.updateTimezone(ctx);
    }
    if (webpageUsername) await ctx.store.dispatch(fetchWebpageInfo({ ctx, domain: webpageUsername, rootDomain }));

    let pageProps = { webpageUsername };
    // ctx.pathname is the page being opened; on client navigation router.route is still the page being left
    if (MyApp.isContentCenter(ctx) && !isContentCenterRoute(ctx.pathname)) {
      if (ctx.res) {
        ctx.res.statusCode = 302;
        ctx.res.setHeader('Location', CONTENT_CENTER_HOME);
      } else {
        // Full load, same as the server redirect, so the user is loaded even when coming from a public page
        window.location.replace(CONTENT_CENTER_HOME);
      }
      return { pageProps };
    }
    if (Component.getInitialProps) {
      pageProps = { ...pageProps, ...(await Component.getInitialProps({ ctx, router })) };
    }
    const safeRedirectPath = sanitizeRedirectPath(redirect_path);
    if (safeRedirectPath && !['/login'].includes(router.route)) {
      destroyCookie(ctx, 'redirect_path', {
        domain: !config().IS_DEV && `.${rootDomain}`
      });
      if (ctx.res) {
        ctx.res.statusCode = 302;
        ctx.res.setHeader('Location', safeRedirectPath);
      } else {
        router.push(safeRedirectPath);
      }
    }
    return { pageProps };
  }

  static isContentCenter = ctx => {
    if (config().CONTENT_CENTER) return true;
    if (!config().IS_DEV_STAGE_DEMO) return false;

    const flag = ctx.store.getState().admin.users.active_feature_flag?.key || parseCookies(ctx)?.domain;
    return flag === 'production';
  };

  static setDomainCookie = async ctx => {
    if (config().IS_DEV_STAGE_DEMO) {
      if (!ctx.store.getState().admin.users.active_feature_flag?.key) {
        await MyApp.fetchActiveFeatureFlag(ctx);
        setCookie(ctx, 'domain', ctx.store.getState().admin.users.active_feature_flag?.key || '', { path: '/' });
      }
    }
  };

  static webpageUsername = async (ctx, rootDomain) => {
    // const result = await ctx.req?.headers?.host?.match(new RegExp(`(?:([^.]+).)${getEnv('NEXT_PUBLIC_WEBPAGE_HOST')}`));
    if (!ctx.req?.headers?.host) return false;
    const result = await ctx.req?.headers?.host?.match(new RegExp(`(?:([^.]+).)${rootDomain}`));
    return result && !FORBIDDEN_PAGES_DOMAINS.includes(result[1]) && result[1];
  };

  static getRootDomain = async ctx => {
    if (!ctx.req?.headers?.host) return null;
    const host = await ctx.req?.headers?.host;
    // Remove protocol if present, then strip port for parsing (e.g. subdomain.localhost:3002)
    let cleanHost = host && host != '' ? host.replace(/^https?:\/\//, '') : host;
    cleanHost = cleanHost.split(':')[0];
    // Localhost only: subdomain.localhost -> root is "localhost" so webpageUsername is extracted
    if (cleanHost && cleanHost.endsWith('.localhost')) return 'localhost';
    // Split by dots and get the last two parts for most common cases
    let parts = cleanHost && cleanHost != '' ? cleanHost.split('.') : host.split('.');
    // Handle cases like localhost, IP addresses, or single word domains
    if (parts.length <= 2) return cleanHost;
    // For most domains, return the last two parts (e.g., example.com from sub.example.com)
    // This handles common TLDs like .com, .org, .net, etc.
    return parts.slice(-2).join('.');
  };

  static fetchActiveFeatureFlag = async ctx => {
    try {
      await ctx.store.dispatch(fetchActiveFeatureFlag({ ctx }));
    } catch (err) {
      await handleUnauthorizedError({ err, ctx });
    }
  };

  static fetchCurrentUser = async ctx => {
    try {
      await ctx.store.dispatch(fetchCurrentUser(ctx));
    } catch (err) {
      await handleUnauthorizedError({ err, ctx });
    }
  };

  static fetchCurrentAccount = async ctx => {
    try {
      await ctx.store.dispatch(fetchCurrentAccount({ ctx }));
    } catch (err) {
      await handleUnauthorizedError({ err, ctx });
    }
  };

  static updateTimezone = async ctx => {
    try {
      let user = ctx.store.getState().currentUser;
      if (user.id && !user.timezone) {
        const timezone = TIMEZONE_LIST.find(el => el.name === Intl.DateTimeFormat().resolvedOptions().timeZone) || null;
        await ctx.store.dispatch(updateTimezone({ ctx, timezone: timezone?.value || timezone }));
      }
    } catch (err) {
      await handleUnauthorizedError({ err, ctx });
    }
  };

  async componentDidMount() {
    await this.props.store.dispatch(health());
    // await this.handleNotificationPermissions();
    // await this.initializeSubscriptions();
    !PUBLIC_ROUTES.includes(this.props.router.route) &&
      !this.props.pageProps?.webpageUsername &&
      config().HELP_BUTTON &&
      HelpButton();

    Router.events.on('routeChangeComplete', this.handlePageView);
    trackPageView(Router.pathname);

    if (typeof window !== 'undefined' && window.sessionStorage?.getItem('mad_just_logged_in')) {
      trackLanding(Router.pathname);
      window.sessionStorage.removeItem('mad_just_logged_in');
    }
  }

  componentWillUnmount() {
    Router.events.off('routeChangeComplete', this.handlePageView);
  }

  handlePageView = () => {
    trackPageView(Router.pathname);
  };

  // async initializeSubscriptions() {
  //   subscribe('sms', (message, data) => this.handleNotificationTrigger(message, data));
  // }

  // handleNotificationPermissions = async () => {
  //   if ('Notification' in window) {
  //     if (window.Notification.permission !== 'granted') {
  //       const permission = await window.Notification.requestPermission();
  //     }
  //   }
  // };

  handleNotificationTrigger = async (message, data) => {
    if ('Notification' in window) {
      if (window.Notification.permission !== 'granted') {
        const permission = await window.Notification.requestPermission();
      }

      if (window.Notification.permission === 'granted') {
        const options = {
          body: data.data,
          icon: 'https://marketaccessdirect.com/crm/images/mad/notifiy.png' // Replace with your notification icon path
        };
        new window.Notification('New Message', options);
      }
    }
  };

  get mainComponent() {
    const { Component, pageProps } = this.props;

    if (config().SSO_FEATURE) {
      return (
        <Auth0Provider>
          <Component {...pageProps} />
        </Auth0Provider>
      );
    } else {
      return <Component {...pageProps} />;
    }
  }

  render() {
    const {
      pageProps: { webpageUsername },
      store
    } = this.props;
    const {
      currentUser: { profile, company, webpage, username, number },
      public: { info }
    } = store.getState();
    const description = profile?.about_me?.replace(/(<([^>]+)>)/gi, '') || '';
    const { name, domain_name } = company || {};
    const image = profile?.personal_photo?.url;
    if (process.env.NODE_ENV !== 'development') {
      Sentry.getCurrentScope().setTag('username', username);
    }

    const isWebpage = !!webpageUsername;

    if (number && number !== this.identifiedNumber && !isWebpage && !PUBLIC_ROUTES.includes(this.props.router.route)) {
      this.identifiedNumber = number;
      posthog.identify(
        number, // Replace 'distinct_id' with your user's unique identifier
        { email: profile?.email, name: `${profile?.first_name} ${profile?.last_name}` } // $set, optional
      );
    }

    return (
      <Provider store={store}>
        <Head>
          <title>{getEnv('NEXT_PUBLIC_PAGE_TITLE')}</title>
          <meta id="viewport" name="viewport" content="initial-scale=1.0, width=device-width" />
          {!isWebpage && company && (
            <>
              <meta name="robots" content="index, follow" />
              <meta name="description" content={description} />
              <meta
                name="keywords"
                content={`${profile.first_name}, ${profile.last_name}, ${name}, ${domain_name}, medical, insurrance, mad, market, access, direct`}
              />
              <link rel="canonical" href={getEnv('NEXT_PUBLIC_APP_URL')} />
              <meta property="og:type" content="article" />
              <meta property="og:locale" content="en_US" />
              <meta property="og:url" content={`//${webpage}`} />
              <meta property="og:title" content={getEnv('NEXT_PUBLIC_PAGE_TITLE')} />
              <meta property="og:description" content={description} />
              <meta property="og:image" content={image} />
              <meta property="og:site_name" content={getEnv('NEXT_PUBLIC_PAGE_TITLE')} />
            </>
          )}
        </Head>
        {!(info?.is_company_webpage && info?.tracking_code) && (
          <>
            {getEnv('NEXT_PUBLIC_GOOGLE_ANALYTICS') && (
              <>
                <Script
                  src={`https://www.googletagmanager.com/gtag/js?id=${getEnv('NEXT_PUBLIC_GOOGLE_ANALYTICS')}`}
                  strategy="afterInteractive"
                />
                <Script
                  id="gtag-init"
                  strategy="afterInteractive"
                  dangerouslySetInnerHTML={{
                    __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${getEnv('NEXT_PUBLIC_GOOGLE_ANALYTICS')}', {
                    page_path: window.location.pathname,
                  });
                `
                  }}
                />
              </>
            )}
          </>
        )}
        {this.mainComponent}
        <WebSocketConnection allowConnection={!PUBLIC_ROUTES.includes(this.props.router.route) && !isWebpage} />

        {!config().IS_DEV_STAGE_DEMO && !config().CONTENT_CENTER && <Dialer />}

        {!isWebpage &&
          !config().CONTENT_CENTER &&
          !CHAT_EXCLUDED_ROUTES.includes(this.props.router.route) &&
          (config(store.getState().currentUser).MAX_AI_V2 ? <AiAgentChat /> : <ChatPopup />)}

        {!isWebpage &&
          !config().CONTENT_CENTER &&
          !CHAT_EXCLUDED_ROUTES.includes(this.props.router.route) &&
          config(store.getState().currentUser).SIDEKICK_FEATURE && <Sidekick />}

        {!isWebpage && !PUBLIC_ROUTES.includes(this.props.router.route) && <VersionUpdateBanner />}
      </Provider>
    );
  }
}

export default withRedux(initStore)(MyApp);
